import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChange, registerUser, signInUser, signOutUser, getCurrentUserData, updateUserData, addCarToUser, updateCar as updateCarInFirebase, removeCar as removeCarFromFirebase, UserData, Car } from '../firebase/auth';

// Use the UserData type from Firebase auth
type User = UserData;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  addCar: (car: Omit<Car, 'id'>) => void;
  updateCar: (carId: string, carData: Partial<Car>) => void;
  removeCar: (carId: string) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userData = await getCurrentUserData(firebaseUser.uid);
          if (userData) {
            setUser(userData);
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error('Error getting user data:', error);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const userData = await signInUser(email, password);
      setUser(userData);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string): Promise<boolean> => {
    try {
      const userData = await registerUser(email, password, firstName, lastName);
      setUser(userData);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await signOutUser();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    if (user) {
      try {
        await updateUserData(user.id, userData);
        const updatedUser = { ...user, ...userData };
        setUser(updatedUser);
      } catch (error) {
        console.error('Update user error:', error);
      }
    }
  };

  const addCar = async (car: Omit<Car, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (user) {
      try {
        await addCarToUser(user.id, car);
        // Refresh user data
        const updatedUserData = await getCurrentUserData(user.id);
        if (updatedUserData) {
          setUser(updatedUserData);
        }
      } catch (error) {
        console.error('Add car error:', error);
      }
    }
  };

  const updateCar = async (carId: string, carData: Partial<Car>) => {
    if (user) {
      try {
        await updateCarInFirebase(user.id, carId, carData);
        // Refresh user data
        const updatedUserData = await getCurrentUserData(user.id);
        if (updatedUserData) {
          setUser(updatedUserData);
        }
      } catch (error) {
        console.error('Update car error:', error);
      }
    }
  };

  const removeCar = async (carId: string) => {
    if (user) {
      try {
        await removeCarFromFirebase(user.id, carId);
        // Refresh user data
        const updatedUserData = await getCurrentUserData(user.id);
        if (updatedUserData) {
          setUser(updatedUserData);
        }
      } catch (error) {
        console.error('Remove car error:', error);
      }
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    addCar,
    updateCar,
    removeCar
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
